'use client';

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';

import { useOfficeHoursAccess } from '@/services/access-control/hooks/useOfficeHoursAccess';

import { JobsIcon, DealsIcon, MembersIcon } from '@/components/core/navbar/components/icons';
import { CalendarBlankIcon } from '@/components/icons';

import type { QuickActionsState } from './utils/resolveQuickActionsState';

import { ActionCard, ActionCardSkeleton } from './components/ActionCard';

import s from './QuickActions.module.scss';
import { useCurrentUserStore } from '@/services/auth/store';

// TODO: Replace with the confirmed external Office Hours scheduling URL
const OH_HREF = '/members?hasOfficeHours=true';

interface QuickActionsProps {
  /**
   * Card set resolved on the server (app/home/page.tsx). Rendering from this
   * rather than from client state is what makes the first paint final — the
   * user store and /me/access both land after hydration, and deriving the
   * cards from them made the band render 2 cards, collapse, then settle on 2-4.
   */
  initial: QuickActionsState | null;
  /**
   * Whether `initial.hasOhAccess` came from /me/access. False when that
   * server-side call failed, in which case Office Hours access falls back to
   * the client query and the band holds its height with skeletons meanwhile.
   */
  ohResolved: boolean;
}

export function QuickActions({ initial, ohResolved }: QuickActionsProps) {
  const { isHydrated } = useCurrentUserStore();
  const { canViewSupply, canSupply, canViewDemand, canRequestDemand, isLoading: ohLoading } = useOfficeHoursAccess();

  // Drives the mobile scroll container's left/right edge fade — only fades
  // the side that still has more cards to reveal. Hooks run unconditionally,
  // ahead of the early return below.
  const gridRef = useRef<HTMLDivElement>(null);
  const [scrollEdges, setScrollEdges] = useState({ canScrollLeft: false, canScrollRight: false });

  const updateScrollEdges = useCallback(() => {
    const el = gridRef.current;
    if (!el) return;
    const canScrollLeft = el.scrollLeft > 1;
    const canScrollRight = el.scrollLeft < el.scrollWidth - el.clientWidth - 1;
    setScrollEdges((prev) =>
      prev.canScrollLeft === canScrollLeft && prev.canScrollRight === canScrollRight
        ? prev
        : { canScrollLeft, canScrollRight },
    );
  }, []);

  // No dependency array: re-checks after every render, so it also catches the
  // ref attaching once real content replaces the skeletons below.
  useEffect(() => {
    updateScrollEdges();
  });

  useEffect(() => {
    window.addEventListener('resize', updateScrollEdges);
    return () => window.removeEventListener('resize', updateScrollEdges);
  }, [updateScrollEdges]);

  // Only null for logged-out visitors, who never render this component at all.
  if (!initial) return null;

  const { group, hasDealsAccess } = initial;

  // Office Hours drives which card leads the 'others' row, so showing the wrong
  // one and swapping it would shift the mobile carousel under a user mid-swipe.
  // With ohResolved the answer is already correct at paint; without it we hold
  // the row's height with skeletons until the client query answers. isHydrated
  // covers the window before the user store fills, where the query is still
  // disabled and so reports isLoading: false without having run.
  const ohFallbackPending = !isHydrated || ohLoading;
  const showSkeletons = !ohResolved && group === 'others' && ohFallbackPending;

  const hasOhAccess = ohResolved ? initial.hasOhAccess : canViewSupply || canSupply || canViewDemand || canRequestDemand;

  const ohCard = (
    <ActionCard
      icon={<CalendarBlankIcon />}
      title="Book Office Hours"
      description="Connect with experts"
      href={OH_HREF}
    />
  );

  return (
    <section className={s.section}>
      <h2 className={s.title}>Quick Actions</h2>
      <div
        ref={gridRef}
        className={s.grid}
        role="region"
        aria-label="Quick actions"
        onScroll={updateScrollEdges}
        style={
          {
            '--fade-left': scrollEdges.canScrollLeft ? '20px' : '0px',
            '--fade-right': scrollEdges.canScrollRight ? '20px' : '0px',
          } as CSSProperties
        }
      >
        {group === 'pl-infra' && (
          <>
            <ActionCard
              icon={<TeamsIcon />}
              title="Teams"
              description="Explore organizations"
              href="/teams?priorities=P1%7CP2%7CP3"
            />
            {ohCard}
            <ActionCard
              icon={<DealsIcon />}
              title="Network Deals"
              description="Exclusive member offers"
              href="/deals"
            />
            <ActionCard icon={<JobsIcon />} title="Job Board" description="Find your next role" href="/jobs" />
          </>
        )}

        {group === 'founder' && (
          <>
            {ohCard}
            {hasDealsAccess && (
              <ActionCard icon={<DealsIcon />} title="Network Deals" description="Member perks" href="/deals" />
            )}
            <ActionCard icon={<JobsIcon />} title="Job Board" description="Find your next role" href="/jobs" />
          </>
        )}

        {group === 'others' && showSkeletons && (
          <>
            <ActionCardSkeleton />
            <ActionCardSkeleton />
          </>
        )}

        {group === 'others' && !showSkeletons && (
          <>
            {hasOhAccess ? (
              ohCard
            ) : (
              <ActionCard
                icon={<MembersIcon />}
                title="Network Directory"
                description="Connect with 3,000+ people"
                href="/members"
              />
            )}
            <ActionCard icon={<JobsIcon />} title="Job Board" description="Find your next role" href="/jobs" />
          </>
        )}
      </div>
    </section>
  );
}

const TeamsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.3375 10.8626C2.40973 10.9168 2.49192 10.9562 2.57938 10.9786C2.66683 11.001 2.75785 11.006 2.84723 10.9932C2.9366 10.9805 3.02259 10.9502 3.10028 10.9042C3.17796 10.8582 3.24583 10.7974 3.3 10.7251C3.71625 10.1701 4.256 9.71968 4.87651 9.40943C5.49702 9.09917 6.18125 8.93765 6.875 8.93765C7.56875 8.93765 8.25298 9.09917 8.87349 9.40943C9.494 9.71968 10.0337 10.1701 10.45 10.7251C10.5595 10.8709 10.7225 10.9671 10.903 10.9928C11.0835 11.0184 11.2667 10.9713 11.4125 10.8618C11.4645 10.8231 11.5105 10.7771 11.5491 10.7251C11.9654 10.1701 12.5051 9.71968 13.1257 9.40943C13.7462 9.09917 14.4304 8.93765 15.1241 8.93765C15.8179 8.93765 16.5021 9.09917 17.1226 9.40943C17.7431 9.71968 18.2829 10.1701 18.6991 10.7251C18.8087 10.871 18.9716 10.9674 19.1522 10.9931C19.3328 11.0188 19.5162 10.9717 19.6621 10.8622C19.8079 10.7527 19.9043 10.5897 19.93 10.4091C19.9557 10.2286 19.9087 10.0452 19.7991 9.89929C19.1906 9.08346 18.3791 8.44113 17.4453 8.03616C17.9572 7.56878 18.3159 6.95755 18.4742 6.28269C18.6325 5.60783 18.5831 4.90087 18.3325 4.25459C18.0818 3.60831 17.6417 3.0529 17.0697 2.66127C16.4978 2.26963 15.8208 2.06006 15.1276 2.06006C14.4344 2.06006 13.7574 2.26963 13.1855 2.66127C12.6135 3.0529 12.1733 3.60831 11.9227 4.25459C11.672 4.90087 11.6226 5.60783 11.7809 6.28269C11.9393 6.95755 12.2979 7.56878 12.8098 8.03616C12.1356 8.32771 11.5231 8.74507 11.0052 9.26593C10.4872 8.74507 9.8747 8.32771 9.20047 8.03616C9.71239 7.56878 10.071 6.95755 10.2294 6.28269C10.3877 5.60783 10.3383 4.90087 10.0876 4.25459C9.83699 3.60831 9.39681 3.0529 8.82486 2.66127C8.25291 2.26963 7.57592 2.06006 6.88273 2.06006C6.18955 2.06006 5.51256 2.26963 4.94061 2.66127C4.36866 3.0529 3.92848 3.60831 3.67783 4.25459C3.42718 4.90087 3.37777 5.60783 3.5361 6.28269C3.69442 6.95755 4.05308 7.56878 4.565 8.03616C3.62712 8.43981 2.81162 9.08255 2.2 9.90015C2.14583 9.97237 2.10642 10.0546 2.08401 10.142C2.0616 10.2295 2.05664 10.3205 2.06941 10.4099C2.08218 10.4993 2.11242 10.5852 2.15842 10.6629C2.20442 10.7406 2.26527 10.8085 2.3375 10.8626ZM15.125 3.43765C15.5329 3.43765 15.9317 3.55861 16.2709 3.78524C16.61 4.01187 16.8744 4.33399 17.0305 4.71086C17.1866 5.08774 17.2275 5.50244 17.1479 5.90252C17.0683 6.30261 16.8719 6.67011 16.5834 6.95856C16.295 7.247 15.9275 7.44344 15.5274 7.52302C15.1273 7.6026 14.7126 7.56176 14.3357 7.40565C13.9588 7.24954 13.6367 6.98519 13.4101 6.64601C13.1835 6.30684 13.0625 5.90807 13.0625 5.50015C13.0625 4.95314 13.2798 4.42853 13.6666 4.04174C14.0534 3.65495 14.578 3.43765 15.125 3.43765ZM6.875 3.43765C7.28292 3.43765 7.68169 3.55861 8.02086 3.78524C8.36004 4.01187 8.6244 4.33399 8.7805 4.71086C8.93661 5.08774 8.97745 5.50244 8.89787 5.90252C8.81829 6.30261 8.62185 6.67011 8.33341 6.95856C8.04496 7.247 7.67746 7.44344 7.27737 7.52302C6.87729 7.6026 6.46259 7.56176 6.08572 7.40565C5.70884 7.24954 5.38672 6.98519 5.16009 6.64601C4.93346 6.30684 4.8125 5.90807 4.8125 5.50015C4.8125 4.95314 5.0298 4.42853 5.41659 4.04174C5.80339 3.65495 6.32799 3.43765 6.875 3.43765ZM17.4453 16.9737C17.9572 16.5063 18.3159 15.8951 18.4742 15.2202C18.6325 14.5453 18.5831 13.8384 18.3325 13.1921C18.0818 12.5458 17.6417 11.9904 17.0697 11.5988C16.4978 11.2071 15.8208 10.9976 15.1276 10.9976C14.4344 10.9976 13.7574 11.2071 13.1855 11.5988C12.6135 11.9904 12.1733 12.5458 11.9227 13.1921C11.672 13.8384 11.6226 14.5453 11.7809 15.2202C11.9393 15.8951 12.2979 16.5063 12.8098 16.9737C12.1356 17.2652 11.5231 17.6826 11.0052 18.2034C10.4872 17.6826 9.8747 17.2652 9.20047 16.9737C9.71239 16.5063 10.071 15.8951 10.2294 15.2202C10.3877 14.5453 10.3383 13.8384 10.0876 13.1921C9.83699 12.5458 9.39681 11.9904 8.82486 11.5988C8.25291 11.2071 7.57592 10.9976 6.88273 10.9976C6.18955 10.9976 5.51256 11.2071 4.94061 11.5988C4.36866 11.9904 3.92848 12.5458 3.67783 13.1921C3.42718 13.8384 3.37777 14.5453 3.5361 15.2202C3.69442 15.8951 4.05308 16.5063 4.565 16.9737C3.62712 17.3773 2.81162 18.02 2.2 18.8376C2.14583 18.9099 2.10642 18.9921 2.08401 19.0795C2.0616 19.167 2.05664 19.258 2.06941 19.3474C2.08218 19.4368 2.11242 19.5227 2.15842 19.6004C2.20442 19.6781 2.26527 19.746 2.3375 19.8001C2.40973 19.8543 2.49192 19.8937 2.57938 19.9161C2.66683 19.9385 2.75785 19.9435 2.84723 19.9307C2.9366 19.918 3.02259 19.8877 3.10028 19.8417C3.17796 19.7957 3.24583 19.7349 3.3 19.6626C3.71625 19.1076 4.256 18.6572 4.87651 18.3469C5.49702 18.0367 6.18125 17.8751 6.875 17.8751C7.56875 17.8751 8.25298 18.0367 8.87349 18.3469C9.494 18.6572 10.0337 19.1076 10.45 19.6626C10.5595 19.8084 10.7225 19.9046 10.903 19.9303C11.0835 19.9559 11.2667 19.9088 11.4125 19.7993C11.4645 19.7606 11.5105 19.7146 11.5491 19.6626C11.9654 19.1076 12.5051 18.6572 13.1257 18.3469C13.7462 18.0367 14.4304 17.8751 15.1241 17.8751C15.8179 17.8751 16.5021 18.0367 17.1226 18.3469C17.7431 18.6572 18.2829 19.1076 18.6991 19.6626C18.8087 19.8085 18.9716 19.9049 19.1522 19.9306C19.3328 19.9563 19.5162 19.9092 19.6621 19.7997C19.8079 19.6902 19.9043 19.5272 19.93 19.3466C19.9557 19.1661 19.9087 18.9827 19.7991 18.8368C19.1906 18.021 18.3791 17.3786 17.4453 16.9737ZM6.875 12.3751C7.28292 12.3751 7.68169 12.4961 8.02086 12.7227C8.36004 12.9494 8.6244 13.2715 8.7805 13.6484C8.93661 14.0252 8.97745 14.4399 8.89787 14.84C8.81829 15.2401 8.62185 15.6076 8.33341 15.8961C8.04496 16.1845 7.67746 16.3809 7.27737 16.4605C6.87729 16.5401 6.46259 16.4993 6.08572 16.3431C5.70884 16.187 5.38672 15.9227 5.16009 15.5835C4.93346 15.2443 4.8125 14.8456 4.8125 14.4376C4.8125 13.8906 5.0298 13.366 5.41659 12.9792C5.80339 12.5924 6.32799 12.3751 6.875 12.3751ZM15.125 12.3751C15.5329 12.3751 15.9317 12.4961 16.2709 12.7227C16.61 12.9494 16.8744 13.2715 17.0305 13.6484C17.1866 14.0252 17.2275 14.4399 17.1479 14.84C17.0683 15.2401 16.8719 15.6076 16.5834 15.8961C16.295 16.1845 15.9275 16.3809 15.5274 16.4605C15.1273 16.5401 14.7126 16.4993 14.3357 16.3431C13.9588 16.187 13.6367 15.9227 13.4101 15.5835C13.1835 15.2443 13.0625 14.8456 13.0625 14.4376C13.0625 13.8906 13.2798 13.366 13.6666 12.9792C14.0534 12.5924 14.578 12.3751 15.125 12.3751Z"
      fill="#1B4DFF"
    />
  </svg>
);
